/**
 * Main Application Entry Point
 * Pool Training System Installation Assistant
 */

import { api } from './services/api.js';
import { showError, showSuccess } from './utils/toast.js';
import { FormStepper } from './components/FormStepper.js';
import { TableForm } from './components/TableForm.js';
import { RoomForm } from './components/RoomForm.js';
import { PreferencesForm } from './components/PreferencesForm.js';
import { ResultsDisplay } from './components/ResultsDisplay.js';

class PoolTrainingApp {
  constructor() {
    this.currentStep = 0;
    this.formData = {
      table: {},
      room: {},
      preferences: {},
    };
    this.results = null;
    this.isValid = {
      table: false,
      room: false,
    };

    this.init();
  }

  init() {
    // Initialize stepper
    this.stepper = new FormStepper('stepper-container', [
      { label: 'Table Info' },
      { label: 'Room Info' },
      { label: 'Preferences' },
      { label: 'Results' },
    ], (step) => {
      // Allow navigation to previous steps only
      if (step < this.currentStep) {
        this.goToStep(step);
      }
    });

    // Initialize forms
    this.tableForm = new TableForm(
      'table-form-container',
      this.formData.table,
      (isValid) => {
        this.isValid.table = isValid;
        this.updateNavigation();
      },
      (data) => {
        this.formData.table = data;
        // Update room form viewer with table data
        if (this.roomForm) {
          this.roomForm.setTableData(data);
        }
        // Update preferences form viewer with table data
        if (this.preferencesForm) {
          this.preferencesForm.setTableData(data);
        }
      },
      this.formData.room // Pass room data for projector positioning
    );

    this.roomForm = new RoomForm(
      'room-form-container',
      this.formData.room,
      (isValid) => {
        this.isValid.room = isValid;
        this.updateNavigation();
      },
      (data) => {
        this.formData.room = data;
        // Update table form viewer with room data
        if (this.tableForm) {
          this.tableForm.setRoomData(data);
        }
        // Update preferences form viewer with room data
        if (this.preferencesForm) {
          this.preferencesForm.setRoomData(data);
        }
      },
      this.formData.table // Pass table data for visualization
    );
    
    // Sync initial data between forms after both are created
    this.tableForm.setRoomData(this.formData.room);
    this.roomForm.setTableData(this.formData.table);

    this.preferencesForm = new PreferencesForm(
      'preferences-form-container',
      this.formData.preferences,
      (data) => {
        this.formData.preferences = data;
      },
      this.formData.table, // Pass table data for visualization
      this.formData.room   // Pass room data for visualization
    );
    
    // Sync initial data to preferences form
    this.preferencesForm.setTableData(this.formData.table);
    this.preferencesForm.setRoomData(this.formData.room);

    // Initialize results display
    this.resultsDisplay = new ResultsDisplay('results-container', () => {
      this.startOver();
    });

    // Set up navigation buttons
    this.setupNavigation();

    // Show first step
    this.goToStep(0);
  }

  setupNavigation() {
    const nextBtn = document.getElementById('next-btn');
    const prevBtn = document.getElementById('prev-btn');

    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.handleNext());
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.handlePrevious());
    }
  }

  goToStep(step) {
    this.currentStep = step;
    this.stepper.goToStep(step);

    // Show/hide form containers
    const containers = [
      'table-form-container',
      'room-form-container',
      'preferences-form-container',
      'results-container',
    ];

    containers.forEach((id, index) => {
      const container = document.getElementById(id);
      if (container) {
        container.style.display = index === step ? 'block' : 'none';
      }
    });

    // Update navigation buttons
    this.updateNavigation();

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  updateNavigation() {
    const nextBtn = document.getElementById('next-btn');
    const prevBtn = document.getElementById('prev-btn');

    if (nextBtn) {
      const canProceed = this.canProceedToNext();
      nextBtn.disabled = !canProceed;
      
      if (this.currentStep === 2) {
        nextBtn.textContent = 'Calculate Recommendations';
        nextBtn.style.display = 'block';
      } else if (this.currentStep === 3) {
        nextBtn.style.display = 'none';
      } else {
        nextBtn.textContent = 'Next';
        nextBtn.style.display = 'block';
      }
    }

    if (prevBtn) {
      // Always show back button, but disable it on first step
      prevBtn.disabled = this.currentStep === 0 || this.currentStep >= 3;
      prevBtn.style.display = this.currentStep >= 3 ? 'none' : 'block';
    }
  }

  canProceedToNext() {
    if (this.currentStep === 0) {
      return this.isValid.table;
    } else if (this.currentStep === 1) {
      return this.isValid.room;
    } else if (this.currentStep === 2) {
      return true; // Preferences are optional
    }
    return false;
  }

  handleNext() {
    if (this.currentStep === 0) {
      if (!this.tableForm.validate(true)) {
        showError('Please fix the errors in the form');
        return;
      }
      this.goToStep(1);
    } else if (this.currentStep === 1) {
      if (!this.roomForm.validate(true)) {
        showError('Please fix the errors in the form');
        return;
      }
      this.goToStep(2);
    } else if (this.currentStep === 2) {
      // Calculate recommendations
      this.calculateRecommendations();
    }
  }

  handlePrevious() {
    if (this.currentStep > 0) {
      this.goToStep(this.currentStep - 1);
    }
  }

  async calculateRecommendations() {
    // Show loading state
    this.goToStep(3);
    this.resultsDisplay.showLoading();

    // Prepare API payload
    const payload = {
      length: this.formData.table.length,
      width: this.formData.table.width,
      height: this.formData.table.height,
      ceiling_height: this.formData.room.ceilingHeight,
      mounting_location: this.formData.room.mountingOption || null,
      location: this.formData.table.location || null,
      // Room considerations
      has_obstructions: this.formData.room.hasObstructions || null,
      needs_cable_management: this.formData.room.needsCableManagement || null,
      has_multiple_tables: this.formData.room.hasMultipleTables || null,
      // Preferences
      additional_notes: this.formData.preferences.additionalNotes || null,
    };

    try {
      const response = await api.post('/calculate', payload);
      this.results = response;
      // Pass form data to results display for PDF download
      this.resultsDisplay.setFormData(this.formData);
      this.resultsDisplay.render(response);
      showSuccess('Recommendations calculated successfully!');
    } catch (error) {
      console.error('Error calculating recommendations:', error);
      this.resultsDisplay.showError(
        error.message || 'Failed to calculate recommendations. Please check your connection and try again.'
      );
      showError(error.message || 'Failed to load recommendations');
    }
  }

  startOver() {
    // Reset form data
    this.formData = {
      table: {},
      room: {},
      preferences: {},
    };
    this.results = null;
    this.isValid = {
      table: false,
      room: false,
    };

    // Reset forms
    this.tableForm.setData({});
    this.roomForm.setData({});
    this.preferencesForm.setData({});

    // Go back to first step
    this.goToStep(0);
    showSuccess('Form reset. You can start over.');
  }
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PoolTrainingApp();
  });
} else {
  new PoolTrainingApp();
}
